<script setup>
import ComparisonTable from "~/components/global/ComparisonTable.vue";
const { isMobile } = useDevice();
const data = await import(
  `../../../public/content/services/development-services/multi-tenant-saas-product-development-content.json`
);

useHead({
  title: data?.meta_tags?.title || "",
  meta: data?.meta_tags?.meta || [],
});
const visible = ref(false);

const formPrefilledData = ref(null);

const openModal = (prefilledValue = null) => {
  if (prefilledValue == null) formPrefilledData.value = null;
  else if (prefilledValue.length) formPrefilledData.value = prefilledValue;
  visible.value = true;
};
</script>

<template>
  <section class="max-w-screen-xl px-4 mx-auto md:mt-10">
    <div class="flex max-md:flex-col justify-between gap-10">
      <WrServicesHeroSection :heading="data?.header.heading" :description="data?.header.text" :src="data?.header.image"
        :alt="data?.header.heading" width="497" height="429" @open-modal="openModal" />
    </div>
  </section>

  <section class="dark:bg-none py-20 lg:py-36">
    <div class="max-w-screen-xl px-4 mx-auto">
      <WrServicesPromiseSection :data="data?.our_promises" width="898" height="898" />
    </div>
  </section>

  <section class="max-w-screen-xl px-4 xl:mt-20 mt-16 relative z-10 mx-auto">
    <div class="max-w-screen-xl m-4">
      <h3 class="font-bold text-center leading-tight relative xl:px-0 lg:px-20">
        <WrGradientText :text="data?.differences?.heading"
          append-class="pb-0 md:pb-2 bg-clip-text bg-[linear-gradient(92.15deg,_var(--text-gradient-1)_-1.57%,_var(--text-gradient-2)_30.72%)]" />
      </h3>
      <p class="xl:mt-4 text-base text-center max-md:px-0 !leading-[1.8]">
        {{ data?.differences?.subheading }}
      </p>
      <div class="table mt-6">
        <ComparisonTable :heading="data?.differences?.heading" :data="data?.differences?.data" />
      </div>
    </div>
  </section>

  <section class="dark:bg-none text-center py-10 px-4">
    <div class="max-w-screen-xl mx-auto px-4 lg:pt-10 lg:pb-16">
      <h1>
        <WrGradientText>
          {{ data?.our_services?.heading }}
        </WrGradientText>
      </h1>
      <p class="text-lg tracking-[0.5px]">
        {{ data?.our_services?.subheading }}
      </p>
    </div>
  </section>
  <section class="dark:bg-none dark:even:bg-none">
    <div class="max-w-screen-xl mx-auto px-4">
      <WrOnboarding :onboarding="data?.our_services?.services" />
    </div>
  </section>




  <section class="dark:bg-none py-10 lg:py-20">
    <div class="max-w-screen-xl mx-auto px-4">
      <h2 class="lg:text-center my-6">
        <WrGradientText append-class="text-3xl lg:text-[48px]">
          {{ data?.multi_tenant_benefits?.heading }}
        </WrGradientText>
      </h2>
      <p class="max-w-screen-lg mx-auto lg:text-center lg:text-xl tracking-[0.5px]">
        {{ data?.multi_tenant_benefits?.subheading }}
      </p>

      <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-20 mt-12">
        <div v-for="benefit in data?.multi_tenant_benefits?.benefit_items">
          <NuxtImg :src="benefit.img" alt="Adonis.js Development Services" width="64" height="64" class="max-h-[64px]"
            loading="lazy" format="webp" />
          <h3 class="text-xl font-bold mt-4">{{ benefit.title }}</h3>
          <p class="mt-2 leading-8">
            {{ benefit.description }}
          </p>
        </div>
      </div>
    </div>
  </section>

  <section class="dark:bg-transparent">
    <div class="max-w-screen-xl mx-auto py-20 px-4 lg:px-8">
      <WrCardCTA :subtitle="data?.project_in_mind?.small_heading" :title="data?.project_in_mind?.heading"
        :description="data?.project_in_mind?.subheading" :icon="data?.project_in_mind?.image"
        append-class="max-md:px-4 py-8" />
    </div>
  </section>


  <!-- Stepper  -->
  <section class="dark:bg-none py-10 lg:py-20">
    <div class="max-w-screen-xl mx-auto px-4">
      <h2 class="lg:text-center">
        <WrGradientText>
          {{ data.development_process.heading }}
        </WrGradientText>
      </h2>
      <p class="max-w-screen-lg mx-auto lg:text-center lg:text-xl tracking-[0.5px] mb-10">
        {{ data.development_process.subheading }}
      </p>
      <!--Section | Our e-commerce solutions to operational needs. Starts-->
      <WrStepper :steps="data.development_process.steps" :layout="isMobile ? 'mobile' : 'desktop'" />
    </div>
  </section>

  <section class="px-4 relative z-10 dark:bg-none py-10 lg:py-20">
    <div class="max-w-screen-xl px-4 mx-auto">
      <h3 class="lg:text-[44px] text-[31px] font-bold text-center leading-tight relative xl:px-0 lg:px-20">
        <WrGradientText append-class="text-3xl lg:text-[48px]">
          {{ data?.technology_stack?.heading }}
        </WrGradientText>
      </h3>
      <span class="absolute size-[14.4px] bg-[#ffdca3] lg:left-10 left-0 top-5 rounded-full block"></span>
      <span class="absolute size-[14.4px] bg-[#ebc6d8] right-20 top-0 rounded-full block"></span>
      <p class="xl:mt-4 text-base text-center max-md:px-0 !leading-[1.8]">
        {{ data?.technology_stack?.subheading }}
      </p>
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <CardTechnologyStack v-for="stack in data?.technology_stack?.stacks" :img="stack.image" :title="stack.title"
          :stacs="stack.list" :url="stack.link" />
      </div>
    </div>
  </section>

  <section>
    <div class="max-w-screen-xl mx-auto px-4 w-full grid md:grid-cols-2 gap-6 relative z-10 pb-10 lg:pb-24 mt-20">
      <div class="max-md:text-center mx-auto max-w-[504px] z-10 relative">
        <h3>
          <WrGradientText text="Get all your questions" append-class="py-0 md:py-4" />
          <span>answered here</span>
        </h3>
        <p class="mt-5 pr-4 text-lg tracking-[0.5px]">
          {{ data?.faq?.subheading }}
        </p>
      </div>
      <div class="grow">
        <WrAccordion :Content="data?.faq?.collapses" />
      </div>
    </div>
  </section>

  <section class="dark:bg-none py-20">
    <div class="max-w-screen-xl px-4 mx-auto flex max-lg:flex-col justify-between gap-14">
      <div class="lg:w-[45%]">
        <h2 class="text-3xl mb-2 max-md:mb-4 md:mb-4">
          <WrGradientText :text="data?.hire_us?.heading"
            append-class="py-0 lg:pb-2 bg-clip-text bg-[linear-gradient(92.15deg,_var(--text-gradient-1)_-1.57%,_var(--text-gradient-2)_30.72%)]" />
        </h2>
        <p class="text-xl max-md:text-sm tracking-[0.5px]">
          {{ data?.hire_us?.subheading }}
        </p>
      </div>
      <div class="grid sm:grid-cols-2 gap-4">
        <!-- Hourly Basis -->
        <template v-for="(card, index) in data?.hire_us?.cards">
          <WrCardHiring
            @openModal="openModal"
            :title="card?.type"
            :description="card?.billing"
            :badgeText="card?.duration"
            :offerText="card?.offerText"
            :icon="card?.image"
            :showCounter="card?.showCounter"
            :defaultCount="parseInt(parseInt(card?.defaultCount))"
            :countLabel="card?.countLabel"
            :page="{ name: 'multi tenant SaaS' }"
            :buttonLabel="card?.buttonLabel"
            :index="index"
          />
        </template>
      </div>
    </div>
  </section>

  <section class="max-w-screen-xl mx-auto px-4 py-6">
    <h2 class="lg:text-center">
      <WrGradientText append-class="leading-[1]">
        Looking for the other services?
      </WrGradientText>
    </h2>
    <p class="max-w-screen-md mx-auto lg:text-lg lg:text-center tracking-[0.5px] mb-5 mt-2">
      Check out our other services that should help you take your digital
      products to the next level and give greater visibility online.
    </p>
    <WrFrameworksCarousel />
  </section>

  <section class="dark:bg-none py-20">
    <div class="max-w-screen-xl mx-auto px-4">
      <h2 class="text-center">
        <WrGradientText append-class="leading-[1]">
          WebReinvent, the company for your software development project.
        </WrGradientText>
      </h2>
      <p class="max-w-screen-lg mx-auto text-center text-xl tracking-[0.5px]">
        Looking for resources? We have them. Looking for services? We have them
        too. Looking for a change? Get in touch with us here.
      </p>

      <div class="grid sm:grid-cols-2 gap-14 lg:gap-44 lg:px-12 mt-12">
        <template v-for="card in data?.call_to_action?.boxes">
          <WrCardCTA :img-position="card.position" :subtitle="card?.small_heading" :icon="card?.image"
            alt="Multi tenat sass Development Services" :title="card?.heading" :description="card?.subheading"
            append-class="pt-36 max-lg:pb-8 lg:py-14 lg:pl-20 pr-8" :button-text="card.buttonText"
            :iconAppendClass="card.iconAppendClass" />
        </template>
      </div>
    </div>
  </section>



  <section class="relative pt-10 lg:pt-20 overflow-hidden max-w-screen-xl mx-auto px-4 z-20">
    <h2 class="text-center mb-11">
      <WrGradientText text="Trusted for excellence and efficiency" />
      <p class="text-lg text-[var(--foreground)] font-anvtgrade-book">
        Webreinvent is trusted by leading organisations to enhance their
        development and operational efficiency.
      </p>
    </h2>
    <!-- Carousel Section -->
    <WrTestimonialCardCarousel />
    <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-7">View All</WrButton>
  </section>
  <WrContactFormPopup :isOpen="visible" @close="visible = false" class="z-30" :prefilledContent="formPrefilledData" />
</template>
